import type { JSX } from 'react';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import { CheckOutlined, DownOutlined, LinkOutlined } from '@ant-design/icons';

import { friendlyError } from '../../lib/api';
import {
  OPENING_TASKS_KEYS,
  openingTasksApi,
  type EquipmentItemDto,
  type EquipmentPatch,
  type MyEquipmentDto,
} from '../../lib/api/openingTasks';
import { Button, Tag } from '../ds';
import {
  draftOf,
  equipmentForTask,
  equipmentProgress,
  nextStatus,
  patchFromDraft,
  replacementBadge,
  TEXT_MAX,
  URL_MAX,
  withEquipmentItem,
  type EquipmentDraft,
} from './equipment-model';
import { safeHttpUrl, shortLinkLabel } from './stage-tasks';
import { fmtDate } from './utils';

/**
 * Спецификация оборудования открытия (FB-34, 02.11.2026) внутри задачи «Заказ оборудования»: УК вносит позиции,
 * партнёр отмечает «Заказано» / «Получено», срок поставки, цену, ссылку и, если нужна замена, пишет на что — её
 * согласует УК (значок «на согласовании УК» / «согласовано» / «отклонено: причина»). Название, описание и
 * количество партнёр не меняет. Тексты выводим как текст (React экранирует), ссылки — только http(s).
 * `readOnly` — режим «смотреть от лица»: всё видно, править нельзя (сервер всё равно ответил бы 403).
 */

/** Один общий запрос на все строки задач (react-query дедуплицирует по ключу). */
export function useMyEquipment(): { data: MyEquipmentDto | undefined } {
  const q = useQuery({ queryKey: OPENING_TASKS_KEYS.equipment, queryFn: () => openingTasksApi.equipment(), retry: false, staleTime: 30_000 });
  return { data: q.data };
}

const READ_ONLY_HINT = 'Режим просмотра — только чтение';

function Check({ checked, label, disabled, onClick, children }: { checked: boolean; label: string; disabled: boolean; onClick: () => void; children: string }): JSX.Element {
  return (
    <button type="button" role="checkbox" className="fk-ot-check" aria-checked={checked} aria-label={label} title={disabled ? READ_ONLY_HINT : undefined} disabled={disabled} onClick={onClick}>
      <span className="fk-ot-check-box" data-checked={checked} aria-hidden="true">
        {checked ? <CheckOutlined /> : null}
      </span>
      <span className="fk-ot-check-text">{children}</span>
    </button>
  );
}

/** Обновить позицию на сервере и в кеше спецификации (ответ сервера — истина). */
function useUpdateItem(): {
  mutate: (v: { id: string; patch: EquipmentPatch }, o?: { onSuccess?: () => void }) => void;
  isPending: boolean;
  pendingId: string | null;
} {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const m = useMutation({
    mutationFn: (v: { id: string; patch: EquipmentPatch }) => openingTasksApi.updateEquipment(v.id, v.patch),
    onSuccess: (updated) => {
      qc.setQueryData<MyEquipmentDto>(OPENING_TASKS_KEYS.equipment, (d) => (d ? withEquipmentItem(d, updated) : d));
    },
    onError: (e) => message.error(friendlyError(e)),
    onSettled: () => qc.invalidateQueries({ queryKey: OPENING_TASKS_KEYS.equipment }),
  });
  return { mutate: (v, o) => m.mutate(v, o), isPending: m.isPending, pendingId: m.isPending ? m.variables.id : null };
}

/** Прогресс «заказано N из M» для строки задачи; задача не та или спецификации нет — null. */
export function EquipmentProgressTag({ taskId }: { taskId: string }): JSX.Element | null {
  const { data } = useMyEquipment();
  const eq = equipmentForTask(data, taskId);
  if (!eq) return null;
  const p = equipmentProgress(eq.items);
  return (
    <Tag color={p.ordered === p.total ? 'green' : 'blue'} title="Спецификация оборудования">
      {p.text}
    </Tag>
  );
}

/** Список позиций спецификации задачи. Нет спецификации — null. */
export function EquipmentPanel({ taskId, readOnly }: { taskId: string; readOnly: boolean }): JSX.Element | null {
  const { data } = useMyEquipment();
  const upd = useUpdateItem();
  const eq = equipmentForTask(data, taskId);
  if (!eq) return null;
  const p = equipmentProgress(eq.items);
  return (
    <section className="fk-ot-eq" aria-label="Спецификация оборудования">
      <div className="fk-ot-eq-head">
        <b>Спецификация оборудования</b>
        <span className="fk-ot-muted">{p.text}</span>
      </div>
      <ul className="fk-ot-eq-list">
        {eq.items.map((it) => (
          <EquipmentRow key={it.id} item={it} readOnly={readOnly} saving={upd.pendingId === it.id} onUpdate={upd.mutate} />
        ))}
      </ul>
    </section>
  );
}

function EquipmentRow({
  item,
  readOnly,
  saving,
  onUpdate,
}: {
  item: EquipmentItemDto;
  readOnly: boolean;
  saving: boolean;
  onUpdate: (v: { id: string; patch: EquipmentPatch }, o?: { onSuccess?: () => void }) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<EquipmentDraft>(() => draftOf(item));
  const [error, setError] = useState<string | null>(null);
  const badge = replacementBadge(item);
  const href = safeHttpUrl(item.url);
  const locked = readOnly || saving;
  const set = (k: keyof EquipmentDraft, v: string): void => {
    setDraft((d) => ({ ...d, [k]: v }));
    setError(null);
  };

  const save = (): void => {
    const r = patchFromDraft(item, draft);
    if ('error' in r) {
      setError(r.error);
      return;
    }
    if (Object.keys(r.patch).length === 0) return;
    onUpdate({ id: item.id, patch: r.patch }, { onSuccess: () => setError(null) });
  };

  const bodyId = `fk-eq-${item.id}`;
  return (
    <li className="fk-ot-eq-item" data-status={item.status}>
      <div className="fk-ot-eq-top">
        <div className="fk-ot-eq-name">
          <b>{item.name}</b>
          <span className="fk-ot-eq-qty">
            {item.qty} {item.unit}
          </span>
        </div>
        <div className="fk-ot-eq-marks">
          <Check checked={item.status !== 'not_ordered'} label={`Заказано: ${item.name}`} disabled={locked} onClick={() => onUpdate({ id: item.id, patch: { status: nextStatus(item.status, 'ordered') } })}>
            Заказано
          </Check>
          <Check checked={item.status === 'received'} label={`Получено: ${item.name}`} disabled={locked} onClick={() => onUpdate({ id: item.id, patch: { status: nextStatus(item.status, 'received') } })}>
            Получено
          </Check>
        </div>
      </div>
      {item.description ? <div className="fk-ot-eq-desc">{item.description}</div> : null}
      <div className="fk-ot-eq-meta">
        {badge ? <Tag color={badge.tone}>{badge.text}</Tag> : null}
        {item.etaDate ? <span>поставка до {fmtDate(item.etaDate)}</span> : null}
        {item.orderedAt && item.status !== 'not_ordered' ? <span>заказано {fmtDate(item.orderedAt)}</span> : null}
        {item.price ? <span>{item.price} ₽</span> : null}
        {href ? (
          <a href={href} target="_blank" rel="noopener noreferrer" title={href}>
            <LinkOutlined aria-hidden="true" /> {shortLinkLabel(href)}
          </a>
        ) : null}
        <button type="button" className="fk-ot-eq-more" aria-expanded={open} aria-controls={bodyId} onClick={() => setOpen((o) => !o)}>
          {readOnly ? 'Подробнее' : 'Замена, цена, сроки'} <DownOutlined aria-hidden="true" />
        </button>
      </div>
      {item.replacementText ? <div className="fk-ot-eq-repl">Замена: {item.replacementText}</div> : null}

      {open ? (
        <div id={bodyId} className="fk-ot-eq-form">
          <label className="fk-ot-eq-field fk-ot-eq-wide">
            <span>На что заменить (если заказываете другое)</span>
            <textarea
              className="fk-ot-input"
              rows={2}
              maxLength={TEXT_MAX}
              value={draft.replacementText}
              disabled={locked}
              placeholder="Например: стол 1500×700 вместо 1200×600"
              onChange={(e) => set('replacementText', e.target.value)}
            />
          </label>
          <label className="fk-ot-eq-field fk-ot-eq-wide">
            <span>Ссылка на товар</span>
            <input className="fk-ot-input" type="url" inputMode="url" maxLength={URL_MAX} value={draft.url} disabled={locked} placeholder="https://" onChange={(e) => set('url', e.target.value)} />
          </label>
          <label className="fk-ot-eq-field">
            <span>Цена, ₽</span>
            <input className="fk-ot-input" inputMode="decimal" maxLength={14} value={draft.price} disabled={locked} onChange={(e) => set('price', e.target.value)} />
          </label>
          <label className="fk-ot-eq-field">
            <span>Дата заказа</span>
            <input className="fk-ot-input" type="date" value={draft.orderedAt} disabled={locked} onChange={(e) => set('orderedAt', e.target.value)} />
          </label>
          <label className="fk-ot-eq-field">
            <span>Срок поставки</span>
            <input className="fk-ot-input" type="date" value={draft.etaDate} disabled={locked} onChange={(e) => set('etaDate', e.target.value)} />
          </label>
          {error ? (
            <div className="fk-ot-cl-err fk-ot-eq-wide" role="alert">
              {error}
            </div>
          ) : null}
          {!readOnly ? (
            <div className="fk-ot-eq-wide">
              <Button variant="primary" size="sm" loading={saving} onClick={save}>
                Сохранить
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
